import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/* SoundBird: opt-in per-instance breakpoint — a dialog-component carrying
   `sb-nonmodal-mobile` stays NON-MODAL ≤749px (native `dialog.show()`
   instead of `showModal()`) so it never covers the header pill, mirroring
   <theme-drawer sb-nonmodal-mobile> (assets/theme-drawer.js) and the
   burger menu's own <details> sheet. With the value "always"
   (sb-nonmodal-mobile="always") the dialog is non-modal at EVERY width —
   used by the search modal (owner feedback 2026-07-09: desktop search must
   not sit in the top layer, whose ::backdrop paints over AND hit-blocks the
   header pill; non-modal keeps the pill crisp + interactive, exactly like
   the cart drawer). Every other dialog-component instance (popup-link,
   cart-disclosure-tooltip, the password-page dialog) is untouched — always
   showModal(), exactly as before. */
const SB_NONMODAL_BREAKPOINT = 750;

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {MediaQueryList | null} */
  #sbMobileQuery = null;

  get #isSbNonModal() {
    return this.hasAttribute('sb-nonmodal-mobile');
  }

  /** @returns {boolean} Whether this dialog should use showModal() right now. */
  get #isModal() {
    if (this.getAttribute('sb-nonmodal-mobile') === 'always') return false;
    if (this.#isSbNonModal && this.#sbMobileQuery?.matches) return false;
    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    if (this.#isSbNonModal) {
      this.#sbMobileQuery = window.matchMedia(`(max-width: ${SB_NONMODAL_BREAKPOINT - 1}px)`);
      this.#sbMobileQuery.addEventListener('change', this.#onSbMobileBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#sbMobileQuery?.removeEventListener('change', this.#onSbMobileBreakpointChange);
    unlockScroll(this.refs.dialog);
  }

  /**
   * SoundBird: re-open in the other mode when crossing 749px while open,
   * mirroring <theme-drawer>'s #onModalBreakpointChange. No animation —
   * the dialog stays visually in place.
   */
  #onSbMobileBreakpointChange = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    removeTrapFocus();
    dialog.close();

    if (this.#isModal) {
      lockScroll(dialog);
      dialog.showModal();
    } else {
      dialog.show();
      trapFocus(dialog);
    }
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      // SoundBird: sb-nonmodal-mobile dialogs use non-modal show() ≤749px so
      // the header pill stays visible/interactive above the sheet (matches
      // <theme-drawer sb-nonmodal-mobile>). show() has no native focus trap,
      // so trapFocus() stands in for it, same as theme-drawer.js's own
      // non-modal path.
      if (this.#isModal) {
        dialog.showModal();
      } else {
        dialog.show();
        trapFocus(dialog);
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    removeTrapFocus();

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
